import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Month calendar for the Site Services page. Renders a 7-column grid; each
 * day cell shows one dot per due event and per visit event. Tapping a day
 * opens a small panel listing that day's providers with quick actions
 * (Log visit — same handler the parent page uses). Overdue days render red.
 *
 * Backed by GET /api/site-services/calendar?month=YYYY-MM.
 */

const KIND_HUE = {
  "Pest Control": "#8A1F1F",
  "Fire Safety": "#E5263F",
  HVAC: "#0A3A7A",
  "Air Conditioning": "#0A3A7A",
  "Tag n Test": "#5B3A99",
  "Cleaning Contract": "#1F6F3E",
  "Car Wash": "#144F82",
  "General Bin": "#7A4A00",
  "Cardboard Bin": "#7A4A00",
  "Dust Extraction Bin": "#7A4A00",
  "Glue Bin": "#7A4A00",
  "Sharpening Services": "#C7A43B",
  "Window Washer": "#228EBF",
  Electrician: "#7A2E00",
  Plumber: "#144F82",
  Other: "#607087",
};

const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, {
    month: "long",
    year: "numeric",
  });
};

const shiftMonth = (yyyymm, delta) => {
  const [y, m] = yyyymm.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const daysGridForMonth = (yyyymm) => {
  const [y, m] = yyyymm.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  // Week starts on Monday to match AU/UK convention.
  const startDay = (first.getDay() + 6) % 7;
  const totalCells = Math.ceil((startDay + last.getDate()) / 7) * 7;
  const cells = [];
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startDay + 1;
    if (dayNum < 1 || dayNum > last.getDate()) {
      cells.push(null);
    } else {
      cells.push(
        `${y}-${String(m).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`,
      );
    }
  }
  return cells;
};

const SiteServiceCalendar = ({ providers, kindFilter, onLogVisit }) => {
  const [month, setMonth] = useState(
    () => new Date().toISOString().slice(0, 7),
  );
  const [data, setData] = useState({ due_events: [], visit_events: [] });
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const todayIso = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    setBusy(true);
    api
      .get("/site-services/calendar", { params: { month } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load calendar"))
      .finally(() => setBusy(false));
  }, [month]);

  // Optionally filter by kind (drives dots + selected-day panel).
  const dueByDay = useMemo(() => {
    const out = {};
    for (const e of data.due_events) {
      if (kindFilter !== "all" && e.kind !== kindFilter) continue;
      (out[e.date] = out[e.date] || []).push(e);
    }
    return out;
  }, [data.due_events, kindFilter]);

  const visitByDay = useMemo(() => {
    const out = {};
    for (const e of data.visit_events) {
      if (kindFilter !== "all" && e.kind !== kindFilter) continue;
      (out[e.date] = out[e.date] || []).push(e);
    }
    return out;
  }, [data.visit_events, kindFilter]);

  const cells = daysGridForMonth(month);
  const selectedDue = selected ? dueByDay[selected] || [] : [];
  const selectedVisits = selected ? visitByDay[selected] || [] : [];

  return (
    <div data-testid="site-services-calendar" className="nb-card p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            data-testid="calendar-prev"
            className="nb-btn bg-white p-2"
            onClick={() => setMonth((m) => shiftMonth(m, -1))}
          >
            <ChevronLeft size={14} strokeWidth={2.5} />
          </button>
          <div className="font-display text-xl min-w-[160px] text-center">
            {monthLabel(month)}
          </div>
          <button
            data-testid="calendar-next"
            className="nb-btn bg-white p-2"
            onClick={() => setMonth((m) => shiftMonth(m, 1))}
          >
            <ChevronRight size={14} strokeWidth={2.5} />
          </button>
          <button
            data-testid="calendar-today"
            className="nb-btn bg-white px-2 py-1 text-xs ml-1"
            onClick={() => setMonth(new Date().toISOString().slice(0, 7))}
          >
            Today
          </button>
        </div>
        <div className="text-[10px] text-[#607087] flex flex-wrap gap-3">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#8A1F1F]" /> Due
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#1F6F3E]" /> Visit logged
          </span>
        </div>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 text-[10px] label-eyebrow text-center border-b-2 border-black">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1 mt-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} className="min-h-[62px]" />;
          const dues = dueByDay[d] || [];
          const vis = visitByDay[d] || [];
          const overdue = dues.length > 0 && d < todayIso;
          const isToday = d === todayIso;
          const isSelected = d === selected;
          return (
            <button
              key={d}
              data-testid={`calendar-day-${d}`}
              onClick={() => setSelected(d)}
              className={`min-h-[62px] text-left rounded-md border-2 p-1 flex flex-col transition ${
                isSelected
                  ? "border-black bg-[#F1E7FA]"
                  : overdue
                    ? "border-[#8A1F1F] bg-[#FCE4E4]"
                    : dues.length
                      ? "border-[#7A4A00] bg-[#FFE9C2]"
                      : "border-[#E5E7EB] bg-white hover:bg-[#F4F8FB]"
              }`}
            >
              <div
                className={`text-xs font-bold ${isToday ? "text-[#228EBF]" : ""}`}
              >
                {Number(d.slice(-2))}
              </div>
              <div className="flex flex-wrap gap-0.5 mt-auto">
                {dues.slice(0, 5).map((e) => (
                  <span
                    key={e.provider_id + e.date}
                    className="w-1.5 h-1.5 rounded-full"
                    title={`${e.name} · ${e.kind} · Due`}
                    style={{
                      background: KIND_HUE[e.kind] || "#8A1F1F",
                    }}
                  />
                ))}
                {vis.slice(0, 5).map((e) => (
                  <span
                    key={"v-" + e.visit_id}
                    className="w-1.5 h-1.5 rounded-full bg-[#1F6F3E]"
                    title={`${e.name} · visit logged`}
                  />
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected-day drawer */}
      {selected && (
        <div className="mt-4 p-3 nb-card-sm bg-[#F4F8FB]" data-testid={`calendar-selected-${selected}`}>
          <div className="flex items-center justify-between">
            <div className="font-display text-sm">
              {new Date(selected).toLocaleDateString(undefined, {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
              {selected < todayIso && selectedDue.length > 0 && (
                <span className="ml-2 text-[10px] text-[#8A1F1F] font-bold">
                  OVERDUE
                </span>
              )}
            </div>
            <button
              onClick={() => setSelected(null)}
              className="text-xs text-[#607087] underline"
            >
              close
            </button>
          </div>
          {selectedDue.length === 0 && selectedVisits.length === 0 ? (
            <div className="text-xs text-[#607087] italic mt-2">
              Nothing scheduled or logged this day.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 mt-2">
              {selectedDue.map((e) => {
                const prov = providers.find((p) => p.id === e.provider_id);
                return (
                  <div
                    key={"d" + e.provider_id}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <div>
                      <b>{e.name}</b>{" "}
                      <span className="text-[#607087]">· {e.kind} · Due</span>
                    </div>
                    <button
                      data-testid={`calendar-log-${e.provider_id}`}
                      onClick={() => onLogVisit?.(prov || { id: e.provider_id, name: e.name })}
                      className="nb-btn nb-btn-dark px-2 py-1 text-[10px]"
                    >
                      Log visit
                    </button>
                  </div>
                );
              })}
              {selectedVisits.map((e) => (
                <div
                  key={"v" + e.visit_id}
                  className="flex items-center justify-between gap-2 text-xs text-[#1F6F3E]"
                >
                  <div>
                    ✓ <b>{e.name}</b>{" "}
                    <span className="text-[#607087]">· visit logged</span>
                  </div>
                  {e.cost != null && (
                    <span className="font-mono-num">
                      ${Number(e.cost).toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {busy && (
        <div className="text-xs text-[#607087] mt-2 italic">Loading…</div>
      )}
    </div>
  );
};

export default SiteServiceCalendar;
